import { format, parseISO } from 'date-fns';
import { Link } from 'react-router-dom';

import { Icon, Typography } from '@/ui';

import { LoadError, PageLoading } from '../PageState';
import { keyInsightPreview, longDate, reflectionPath } from '../format';

import {
  useGetCoachingNotesHomeQuery,
  useGetMcpConnectGrantsQuery,
} from '@/store/api/mcpMemberApi';

const rowClass =
  'grid grid-cols-[96px_minmax(0,1fr)_24px] items-center gap-5 py-4 text-cement-500 no-underline';

const MomentsHomePage = () => {
  const home = useGetCoachingNotesHomeQuery();
  const grants = useGetMcpConnectGrantsQuery();
  if (home.isLoading) return <PageLoading />;
  if (home.isError || !home.data) {
    return (
      <LoadError
        onRetry={() => {
          home.refetch().catch(() => undefined);
        }}
      />
    );
  }
  const { next_session: next, sessions, topic_ideas: topics } = home.data;
  const connected = grants.data?.grants[0];

  return (
    <main className="mx-auto w-full max-w-[760px] px-4 pb-28 pt-8 md:px-10">
      <nav
        aria-label="Your AI"
        className="flex flex-wrap items-center justify-end gap-x-4 gap-y-1 text-sm text-cement-300"
      >
        <Icon>public_link</Icon>
        <Link className="text-sage-500" to="/m/settings/coaching-notes">
          Coaching notes
        </Link>
        <Link className="text-sage-500" to="/m/settings/ai-moments">
          When it comes up
        </Link>
        {/* Unknown grants (loading or failed) must not read as "not connected". */}
        {grants.data && (
          <Link
            className="inline-flex items-center gap-1.5 text-sage-500"
            to="/m/settings/connect-ai"
          >
            {connected ? (
              <>
                <span
                  aria-hidden="true"
                  className="size-2 rounded-full bg-sage-500"
                />
                {connected.client_name}
              </>
            ) : (
              'Connect your AI'
            )}
          </Link>
        )}
      </nav>
      {next && (
        <>
          <Typography
            className="mb-3 mt-6 text-lg font-semibold"
            component="h2"
            variant="h6"
          >
            Next session
          </Typography>
          <Link
            aria-label="Read topic ideas for your next session"
            className="grid grid-cols-[96px_minmax(0,1fr)_24px] items-center gap-5 rounded-[18px] bg-sage-200 py-4 pl-5 pr-4 text-cement-500 no-underline"
            to="/m/topics"
          >
            <span className="font-semibold text-sage-500">
              {format(parseISO(next.starts_at), 'MMM d')}
            </span>
            <span className="min-w-0">
              <span className="block text-lg font-semibold">
                {format(parseISO(next.starts_at), 'EEEE, h:mm a')} with{' '}
                {next.coach_first_name}
              </span>
              {topics.length > 0 && (
                <span className="block truncate text-cement-400">
                  Topic ideas:{' '}
                  {topics.map((topic) => topic.headline).join(', ')}
                </span>
              )}
            </span>
            <Icon className="text-sage-500">chevron_right</Icon>
          </Link>
        </>
      )}
      <Typography
        className="mb-1 mt-8 text-lg font-semibold"
        component="h2"
        variant="h6"
      >
        Moments
      </Typography>
      {sessions.length === 0 && (
        <p className="text-cement-400">Nothing here right now.</p>
      )}
      <ol className="m-0 list-none p-0">
        {sessions.map((moment) => (
          <li
            key={moment.session.session_id}
            className="border-0 border-t border-solid border-cement-200 first-of-type:border-t-0"
          >
            <Link
              aria-label={`Open the moment from ${longDate(moment.session)}`}
              className={rowClass}
              to={reflectionPath(moment.session)}
            >
              <span className="text-cement-400">{moment.session.label}</span>
              <span>{keyInsightPreview(moment.key_insight)}</span>
              <Icon className="text-cement-300">chevron_right</Icon>
            </Link>
          </li>
        ))}
      </ol>
    </main>
  );
};

export default MomentsHomePage;
